import { Box, IconButton, MenuItem, MenuList, Popover, TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memo, MouseEvent, useState } from 'react';
import { cast } from '~/utils/helper';
import {
  EditLineIcon,
  CallIcon,
  MessageIcon,
  EquipmentIcon,
  MapCircleIcon,
  DeleteIcon,
  Information4Icon,
  Delete1Icon,
  Information5Icon,
  Equipment2Icon,
  MapCircle2Icon,
  EditLine2Icon,
} from '~/assets/icons';
import dayjs from 'dayjs';
import BaseTable from '~/components/common/base-table/BaseTable';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import { ActiveEnum, ImeiEnum, StatusEnum } from '~/utils/enum';
import { IPerson } from '~/utils/interface/person';
import { personActions } from '~/thunks/person/personSlice';
import { useCallContext } from '~/context/CallContext';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from '../Person.module.scss';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

const cx = classNames.bind(styles);

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#404854',
    boxShadow: '0px 4px 12px #509EE833',
    border: 1,
    fontSize: theme.typography.pxToRem(15),
  },
}));

type PersonListProps = {
  onOpenEdit: (row: IPerson) => void;
  onOpenActive?: (row: IPerson) => void;
  onCallDevice?: (row: IPerson) => void;
  onSendMessage?: (row: IPerson) => void;
  canSendMessage?: boolean;
  onAssignDevice?: (row: any) => void;
  onExchangeDevice?: (row: any) => void;
  onUnassignDevice?: (row: any) => void;
  onAssignLocation?: (row: any) => void;
  onUnAssignLocation?: (row: any) => void;
};

const PersonList = (props: PersonListProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isActiveCallVisible } = useCallContext();

  const { t } = useTranslation('admin/person');

  const { persons, totalItems, filter } = useAppSelector((state) => state.person);

  const {
    onOpenEdit,
    onOpenActive,
    onCallDevice,
    onSendMessage,
    canSendMessage,
    onAssignDevice,
    onExchangeDevice,
    onUnassignDevice,
    onAssignLocation,
    onUnAssignLocation,
  } = props;

  const equipmentMenuActions = [
    { key: 'assignDevice', label: t('personTable.equipmentMenu.assignDevice', { ns: 'admin/common' }) },
    { key: 'changeDevice', label: t('personTable.equipmentMenu.changeDevice', { ns: 'admin/common' }) },
    { key: 'cancelAssignDevice', label: t('personTable.equipmentMenu.cancelAssignDevice', { ns: 'admin/common' }) },
  ];

  const [person, setPerson] = useState<IPerson>();

  const [equipmentAnchorEl, setEquipmentAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEquipmentRow, setSelectedEquipmentRow] = useState<any | null>(null);

  const [assignLocationAnchorEl, setAssignLocationAnchorEl] = useState<null | HTMLElement>(null);

  const headCells: Array<HeadCelType> = [
    {
      id: 'stt',
      label: t('stt'),
      sortable: false,
      searchable: false,
      className: 'w-12 border-l border-[#EBF1F8] border-b-0 sticky left-0 z-10',
    },
    {
      id: 'action',
      label: t('action', { ns: 'admin/common' }),
      sortable: false,
      searchable: false,
      className: 'border-l border-white border-b-0 sticky left-12 z-10',
    },
    {
      id: 'code',
      label: t('code'),
      sortable: true,
      searchable: true,
      className: 'w-40 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'cccd',
      label: t('cccd.title'),
      sortable: true,
      searchable: true,
      className: 'w-40 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'full_name',
      label: t('full_name.title'),
      sortable: true,
      searchable: true,
      className: 'w-52 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'imei',
      label: t('imei'),
      sortable: true,
      searchable: true,
      className: 'w-48 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'ward_name',
      label: t('province_ward.title'),
      sortable: false,
      searchable: false,
      className: 'w-56 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'active',
      label: t('status.title'),
      sortable: true,
      searchable: false,
      className: 'w-44 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'geofenceStatus',
      label: t('geofenceStatus.title'),
      sortable: true,
      searchable: false,
      className: 'w-40 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'assignStatus',
      label: t('assignStatus.title'),
      sortable: true,
      searchable: false,
      className: 'w-36 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'createduser',
      label: t('createduser'),
      sortable: true,
      searchable: true,
      className: 'w-48 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'number_of_violations',
      label: t('number_of_violations'),
      sortable: true,
      searchable: true,
      className: 'w-36 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'createddate',
      label: t('createddate'),
      sortable: true,
      searchable: false,
      className: 'w-40 border-l border-[#EBF1F8] border-b-0',
    },
  ];

  const styleStatus = (active: any) => {
    if (active === StatusEnum.INACTIVE) {
      return (
        <Box className='text-[#C62828] bg-[#C628281A] rounded-full text-base font-normal w-fit py-2 px-3'>
          {t('status.' + StatusEnum.INACTIVE)}
        </Box>
      );
    }

    return (
      <Box className='text-[#17A62B] bg-[#00B1351A] rounded-full text-base font-normal w-fit py-2 px-3'>
        {t('status.' + StatusEnum.ACTIVE)}
      </Box>
    );
  };

  const styleGeofenceStatus = (row: IPerson) => {
    if (row.geofence_ids?.length) {
      return (
        <Box className='text-[#17A62B] bg-[#00B1351A] rounded-full text-base font-normal w-fit py-2 px-3'>
          {t('geofenceStatus.1')}
        </Box>
      );
    }

    return (
      <Box className='text-[#7B8794] bg-[#7B87941A] rounded-full text-base font-normal w-fit py-2 px-3'>
        {t('geofenceStatus.0')}
      </Box>
    );
  };

  const styleAssignStatus = (row: IPerson) => {
    if (row.imei) {
      return (
        <Box className='text-[#17A62B] bg-[#00B1351A] rounded-full text-base font-normal w-fit py-2 px-3'>
          {t('assignStatus.' + ImeiEnum.ASSIGNED)}
        </Box>
      );
    }

    return (
      <Box className='text-[#7B8794] bg-[#7B87941A] rounded-full text-base font-normal w-fit py-2 px-3'>
        {t('assignStatus.' + ImeiEnum.UNASSIGNED)}
      </Box>
    );
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      personActions.setFilter({
        pageIndex: newPage,
      })
    );
  };

  const handleChangePageSize = (pageSize: number) => {
    dispatch(
      personActions.setFilter({
        pageSize: pageSize,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSort = (sortBy: string, sortOrder: SortDirection) => {
    dispatch(
      personActions.setFilter({
        sortBy: sortBy,
        sortOrder: sortOrder,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSearch = (searchBy: string, value: string) => {
    dispatch(
      personActions.setFilter({
        search: value,
        searchBy: searchBy,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const openEquipmentMenu = (row: any) => (event: MouseEvent<HTMLElement>) => {
    setSelectedEquipmentRow(row);
    setEquipmentAnchorEl(event.currentTarget);
  };

  const closeEquipmentMenu = () => {
    setEquipmentAnchorEl(null);
    setSelectedEquipmentRow(null);
  };

  const selectedRowHasImei = Boolean(selectedEquipmentRow?.imei && String(selectedEquipmentRow.imei).trim() !== '');

  const handleEquipmentMenuAction = (actionKey: string) => {
    if (!selectedEquipmentRow) {
      closeEquipmentMenu();
      return;
    }

    if (actionKey === 'assignDevice') {
      onAssignDevice?.(selectedEquipmentRow);
      closeEquipmentMenu();
      return;
    }

    if (actionKey === 'changeDevice') {
      onExchangeDevice?.(selectedEquipmentRow);
      closeEquipmentMenu();
      return;
    }

    if (actionKey === 'cancelAssignDevice') {
      onUnassignDevice?.(selectedEquipmentRow);
      closeEquipmentMenu();
      return;
    }

    closeEquipmentMenu();
  };

  const hanldeAssignLocationPopup = (row: IPerson) => (event: MouseEvent<HTMLElement>) => {
    setAssignLocationAnchorEl(event.currentTarget);
    setPerson(row);
  };

  const handleCloseAssignLocationPopup = () => {
    setAssignLocationAnchorEl(null);
  };

  return (
    <Box className='w-full'>
      <BaseTable<any>
        label={t('person1')}
        data={persons}
        pageIndex={filter.pageIndex || PAGE_INDEX}
        pageSize={filter.pageSize || PAGE_SIZE}
        totalItems={totalItems}
        headCells={headCells}
        sortBy={filter.sortBy}
        sortOrder={filter.sortOrder}
        searchInput={filter.search}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
        onRequestSort={handleRequestSort}
        onRequestSearch={handleRequestSearch}
        renderCell={(row: IPerson, index: number) => {
          return (
            <>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white' }}
              >
                {index + PAGE_INDEX + (Number(filter.pageIndex || 0) - PAGE_INDEX) * PAGE_SIZE}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ position: 'sticky', left: 48, zIndex: 1, backgroundColor: 'white' }}
              >
                <Box className='flex justify-between'>
                  {row.active === ActiveEnum.OPEN ? (
                    <LightTooltip title={t('personTable.hover.detail', { ns: 'admin/common' })}>
                      <IconButton onClick={() => navigate(`/admin/persons/${row.id}`)}>
                        <Information4Icon />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <IconButton disabled>
                      <Information5Icon />
                    </IconButton>
                  )}
                  {row.imei && row.active === ActiveEnum.OPEN ? (
                    <LightTooltip
                      title={isActiveCallVisible ? t('tooltip.callDisabledWhileActive') : t('tooltip.call')}
                    >
                      <IconButton
                        onClick={() => onCallDevice?.(row)}
                        disabled={isActiveCallVisible}
                        aria-label={t('tooltip.call')}
                      >
                        <CallIcon stroke={isActiveCallVisible ? '#7B8794' : '#E48A1D'} />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <IconButton disabled aria-label={t('tooltip.call')}>
                      <CallIcon stroke='#7B8794' />
                    </IconButton>
                  )}
                  {canSendMessage ? (
                    row.active === ActiveEnum.OPEN ? (
                      <LightTooltip title={t('message.title.single')}>
                        <IconButton onClick={() => onSendMessage?.(row)} aria-label='send-message'>
                          <MessageIcon />
                        </IconButton>
                      </LightTooltip>
                    ) : (
                      <IconButton disabled aria-label='send-message-disabled'>
                        <MessageIcon />
                      </IconButton>
                    )
                  ) : null}
                  {row.active === ActiveEnum.OPEN ? (
                    <LightTooltip title={t('tooltip.assignOrExchangeOrCancelDevice')}>
                      <IconButton onClick={openEquipmentMenu(row)}>
                        <EquipmentIcon />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <IconButton disabled>
                      <Equipment2Icon />
                    </IconButton>
                  )}
                  {row.active === ActiveEnum.OPEN ? (
                    <LightTooltip title={t('tooltip.assign')}>
                      <IconButton onClick={hanldeAssignLocationPopup(row)}>
                        <MapCircleIcon />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <IconButton disabled>
                      <MapCircle2Icon />
                    </IconButton>
                  )}
                  {row.active === ActiveEnum.OPEN ? (
                    <LightTooltip title={t('tooltip.edit')}>
                      <IconButton onClick={() => onOpenEdit?.(row)}>
                        <EditLineIcon />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <IconButton disabled>
                      <EditLine2Icon className='w-[18px] h-[18px]' />
                    </IconButton>
                  )}
                  {row.active === ActiveEnum.OPEN ? (
                    <LightTooltip title={t('tooltip.inactive')}>
                      <IconButton onClick={() => onOpenActive?.(row)}>
                        <Delete1Icon />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <LightTooltip title={t('tooltip.active')}>
                      <IconButton onClick={() => onOpenActive?.(row)}>
                        <DeleteIcon />
                      </IconButton>
                    </LightTooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.code)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.cccd)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.full_name)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.imei)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >{`${cast(row.ward_name ? row.ward_name + ' - ' : '')} ${cast(row.province_name)}`}</TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]'>
                <Box className='flex justify-center'>{styleStatus(row.active)}</Box>
              </TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]'>
                <Box className='flex justify-center'>{styleGeofenceStatus(row)}</Box>
              </TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]'>
                <Box className='flex justify-center'>{styleAssignStatus(row)}</Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.createduser)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.number_of_violations)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {row.createddate ? dayjs(row.createddate).format('DD/MM/YYYY') : ''}
              </TableCell>
            </>
          );
        }}
      />

      <Popover
        open={Boolean(equipmentAnchorEl)}
        anchorEl={equipmentAnchorEl}
        onClose={closeEquipmentMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{ className: cx('equipment-popover') }}
      >
        <MenuList disablePadding className={cx('equipment-menu-list')}>
          {equipmentMenuActions.map((action) => {
            const isAssignBlocked = action.key === 'assignDevice' && selectedRowHasImei;
            const isChangeBlocked = action.key === 'changeDevice' && !selectedRowHasImei;
            const isUnassignBlocked = action.key === 'cancelAssignDevice' && !selectedRowHasImei;
            const isActionBlocked = isAssignBlocked || isChangeBlocked || isUnassignBlocked;

            return (
              <MenuItem
                key={action.key}
                onClick={() => handleEquipmentMenuAction(action.key)}
                disabled={isActionBlocked}
              >
                {action.label}
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>

      <Popover
        open={Boolean(assignLocationAnchorEl)}
        anchorEl={assignLocationAnchorEl}
        onClose={handleCloseAssignLocationPopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{ className: cx('equipment-popover') }}
      >
        <MenuList disablePadding className={cx('equipment-menu-list')}>
          <MenuItem
            key={1}
            onClick={() => {
              onAssignLocation?.(person);
              handleCloseAssignLocationPopup();
            }}
          >
            {t('location.add')}
          </MenuItem>
          <MenuItem
            key={2}
            disabled={person?.geofence_ids?.length === 0}
            onClick={() => {
              onUnAssignLocation?.(person);
              handleCloseAssignLocationPopup();
            }}
          >
            {t('location.remove')}
          </MenuItem>
        </MenuList>
      </Popover>
    </Box>
  );
};

export default memo(PersonList);
